import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertOctagon, Bookmark, Search, Trash2, UserPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  InlineAlert,
  Input,
  MultiSelect,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type {
  BugSeverity,
  EmployeeSummary,
  PageResponse,
  Priority,
  ProjectSummary,
  SavedSearchView,
  SearchCriteria,
  WorkItemStatus,
  WorkItemSummary,
} from "../../shared/types/api";
import { BLANK, isBlank, toParams, UNASSIGNED } from "./searchCriteria";
import { WorkItemTypeBadge } from "./WorkItemTypeBadge";
import {
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  PRIORITY_TONE,
  SEVERITY_LABELS,
  STATUS_LABELS,
  STATUS_ORDER,
  formatHours,
  formatSlippage,
  isClosedStatus,
} from "./workItemVocabulary";

const SEVERITIES: BugSeverity[] = ["CRITICAL", "MAJOR", "MINOR", "TRIVIAL"];
const PAGE_SIZE = 50;

/**
 * Search across everything, on any combination of criteria (§119).
 *
 * <p>The filters do not run as they change. Five dropdowns that each fire a query turn setting up
 * one question into five answers, four of which nobody wanted; the Search button is what says the
 * question is finished.
 *
 * <p>Within a filter, choices widen the search; between filters, they narrow it. That is what
 * people mean without being told, and it is the rule the server enforces.
 */
export function SearchPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<SearchCriteria>(BLANK);
  /** What was actually asked. Null until Search is pressed for the first time. */
  const [applied, setApplied] = useState<SearchCriteria | null>(null);
  const [chosenTemplate, setChosenTemplate] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  /** The row whose claim is waiting to be confirmed, if any. */
  const [asking, setAsking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>(
        "/api/v1/projects?size=200&sort=name,asc",
      ),
    staleTime: 5 * 60 * 1000,
  });

  const people = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    staleTime: 5 * 60 * 1000,
  });

  const templates = useQuery({
    queryKey: ["saved-searches"],
    queryFn: () => api.get<SavedSearchView[]>("/api/v1/saved-searches"),
  });

  const results = useQuery({
    queryKey: ["work-item-search", applied],
    queryFn: () =>
      api.get<PageResponse<WorkItemSummary>>(
        `/api/v1/work-items/search?${toParams(applied!, PAGE_SIZE)}`,
      ),
    enabled: applied != null,
  });

  const saveTemplate = useMutation({
    mutationFn: (name: string) =>
      api.post<SavedSearchView>("/api/v1/saved-searches", {
        name,
        criteria: draft,
      }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["saved-searches"] });
      setChosenTemplate(saved.id);
      setNotice(`Saved as “${saved.name}”.`);
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save that search.",
      ),
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/saved-searches/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["saved-searches"] });
      setChosenTemplate("");
      setNotice("Saved search deleted.");
    },
  });

  const assignToMe = useMutation({
    mutationFn: (workItemId: string) =>
      api.post(`/api/v1/work-items/${workItemId}/assignee`, {
        assigneeId: user!.employeeId,
      }),
    onSuccess: () => {
      // The results are now out of date in one row, and re-running is cheaper than patching it.
      void queryClient.invalidateQueries({ queryKey: ["work-item-search"] });
      void queryClient.invalidateQueries({ queryKey: ["my-work"] });
      setNotice("Assigned to you.");
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not assign that item.",
      ),
  });

  const runSearch = () => {
    setError(null);
    setNotice(null);
    setApplied({ ...draft });
  };

  const applyTemplate = (id: string) => {
    setChosenTemplate(id);
    setError(null);
    setNotice(null);
    if (id === "") {
      return;
    }
    const template = templates.data?.find((each) => each.id === id);
    if (!template) {
      return;
    }
    // Merged over BLANK so a template saved before a filter existed still loads.
    const criteria: SearchCriteria = { ...BLANK, ...template.criteria };
    setDraft(criteria);
    setApplied(criteria);
  };

  const reset = () => {
    setDraft(BLANK);
    setApplied(null);
    setChosenTemplate("");
    setError(null);
    setNotice(null);
  };

  const rows = results.data?.content ?? [];
  const total = results.data?.totalElements ?? 0;
  const canAssign = can("WORK_ITEM_ASSIGN");

  /* A question belongs to the row it was asked about. If the results change underneath it — a new
     search, or a refetch after somebody else claimed something — that row may be gone, and a
     button left armed over whatever slid into its place is the mistake the step exists to stop. */
  useEffect(() => {
    const present = results.data?.content ?? [];
    setAsking((current) =>
      current !== null && !present.some((row) => row.id === current) ? null : current,
    );
  }, [results.data]);
  const nothingAsked = isBlank(draft);

  const assigneeOptions = [
    { value: UNASSIGNED, label: "Unassigned" },
    ...(people.data?.content ?? []).map((person) => ({
      value: person.id,
      label: person.fullName,
      hint: person.employeeCode,
    })),
  ];

  const assigneeSelection = [
    ...(draft.unassigned ? [UNASSIGNED] : []),
    ...draft.assigneeIds,
  ];

  return (
    <>
      <PageHeader
        title="Search"
        subtitle="Across every project. Choices inside a filter widen the search; the filters narrow each other."
      />

      {error && (
        <div className="mb-4">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}
      {notice && !error && (
        <div className="mb-4">
          <InlineAlert tone="success">{notice}</InlineAlert>
        </div>
      )}

      <Card className="mb-4">
        <CardBody className="space-y-4">
          {/*
            One line, filters and actions together. The buttons on their own row below read as a
            separate step, and the divider above them made the card look like two cards. `items-end`
            keeps the controls aligned along their bottoms despite the labels above them.
          */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-28 flex-1">
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Key or title
              </label>
              <Input
                value={draft.q}
                onChange={(event) =>
                  setDraft({ ...draft, q: event.target.value })
                }
                onKeyDown={(event) => event.key === "Enter" && runSearch()}
                placeholder="LMS-14, patient…"
                className="h-9"
              />
            </div>

            <div className="min-w-28 flex-1">
              <MultiSelect
                label="Project"
                searchable
                options={(projects.data?.content ?? []).map((project) => ({
                  value: project.id,
                  label: project.name,
                  hint: project.projectKey,
                }))}
                selected={draft.projectIds}
                onChange={(projectIds) => setDraft({ ...draft, projectIds })}
              />
            </div>

            <div className="min-w-28 flex-1">
              <MultiSelect
                label="Status"
                emptyLabel="Any open"
                options={STATUS_ORDER.map((status) => ({
                  value: status,
                  label: STATUS_LABELS[status],
                }))}
                selected={draft.statuses}
                onChange={(statuses) =>
                  setDraft({ ...draft, statuses: statuses as WorkItemStatus[] })
                }
              />
            </div>

            <div className="min-w-28 flex-1">
              <MultiSelect
                label="Assigned to"
                searchable
                options={assigneeOptions}
                selected={assigneeSelection}
                onChange={(chosen) =>
                  setDraft({
                    ...draft,
                    unassigned: chosen.includes(UNASSIGNED),
                    assigneeIds: chosen.filter((value) => value !== UNASSIGNED),
                  })
                }
              />
            </div>

            <div className="min-w-28 flex-1">
              <MultiSelect
                label="Severity"
                emptyLabel="Any (bugs)"
                options={SEVERITIES.map((severity) => ({
                  value: severity,
                  label: SEVERITY_LABELS[severity],
                }))}
                selected={draft.severities}
                onChange={(severities) =>
                  setDraft({
                    ...draft,
                    severities: severities as BugSeverity[],
                  })
                }
              />
            </div>

            <div className="min-w-28 flex-1">
              <MultiSelect
                label="Priority"
                options={PRIORITY_ORDER.map((priority) => ({
                  value: priority,
                  label: PRIORITY_LABELS[priority],
                }))}
                selected={draft.priorities}
                onChange={(priorities) =>
                  setDraft({ ...draft, priorities: priorities as Priority[] })
                }
              />
            </div>

            {/* On the same line as the filters and the buttons. These sat in a row of their own
                above the card, which spent a whole line saying nothing until somebody had a
                search worth saving. `ml-auto` keeps them at the far end, so Search and Reset
                stay where the eye already looks for them however many filters are on show. */}
            <div className="ml-auto flex shrink-0 items-center gap-2">
            <div className="w-44">
              <Select
                value={chosenTemplate}
                onChange={(event) => applyTemplate(event.target.value)}
                aria-label="Saved searches"
              >
                <option value="">Saved searches…</option>
                {(templates.data ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </Select>
            </div>

            <SaveSearchControl
              disabled={nothingAsked}
              saving={saveTemplate.isPending}
              loadedName={
                templates.data?.find((each) => each.id === chosenTemplate)?.name ?? ""
              }
              existingNames={(templates.data ?? []).map((each) => each.name)}
              onSave={(name) => saveTemplate.mutate(name)}
            />

            {chosenTemplate !== "" && (
              <Button
                variant="ghost"
                aria-label="Delete this saved search"
                title="Delete this saved search"
                icon={<Trash2 className="h-4 w-4" />}
                loading={deleteTemplate.isPending}
                onClick={() => deleteTemplate.mutate(chosenTemplate)}
              />
            )}
          
              <Button
                onClick={runSearch}
                disabled={nothingAsked}
                icon={<Search className="h-4 w-4" />}
                className="h-9"
              >
                Search
              </Button>
              <Button variant="secondary" onClick={reset} className="h-9">
                Reset
              </Button>
            </div>
          </div>

          {draft.statuses.length === 0 && (
            <p className="text-xs text-ink-500">
              Finished work is left out. Choose Done or Cancelled in Status to search it.
            </p>
          )}

          {draft.severities.length > 0 && (
            <p className="text-xs text-ink-500">
              Only bugs carry a severity, so this narrows the results to bugs.
            </p>
          )}
        </CardBody>
      </Card>

      {applied == null ? (
        <Card>
          <EmptyState
            title="Nothing searched yet"
            message="Choose any combination of filters and press Search. An empty form is not a question, so nothing is listed until you ask one."
          />
        </Card>
      ) : results.isPending ? (
        <Card>
          <CardBody className="flex items-center gap-3 text-sm text-ink-600">
            <Spinner className="h-4 w-4" />
            Searching…
          </CardBody>
        </Card>
      ) : results.isError ? (
        <ErrorState
          error={results.error}
          onRetry={() => void results.refetch()}
        />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            title="Nothing matches"
            message="No work item anywhere fits all of those filters together. Removing one usually finds it."
          />
        </Card>
      ) : (
        <>
          <p className="mb-2 px-1 text-sm text-ink-600">
            {total} {total === 1 ? "result" : "results"}
            {total > rows.length && `, showing the first ${rows.length}`}.
          </p>

          <Card className="overflow-hidden">
            <ul>
              {rows.map((item) => (
                <li
                  key={item.id}
                  className="border-b border-ink-100 last:border-0"
                >
                  <div className="flex flex-wrap items-center gap-3 px-5 py-2.5 transition-colors hover:bg-ink-50">
                    <button
                      type="button"
                      onClick={() => navigate(`/work-items/${item.itemKey}`)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <WorkItemTypeBadge type={item.workItemType} />
                      <span
                        className="shrink-0 rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-ink-600"
                        title={item.projectName}
                      >
                        {item.projectKey}
                      </span>
                      <span className="shrink-0 font-mono text-xs text-ink-500">
                        {item.itemKey}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                        {item.title}
                      </span>
                    </button>

                    {/*
                      One cluster on the right, fixed widths, nothing that can wrap. Six items
                      strung across the row with the button sizing itself was what made this
                      screen look chaotic — the button grew to two lines and dragged the rest
                      out of alignment.
                    */}
                    <div className="flex shrink-0 items-center gap-3 text-xs">
                      {item.openBlockerCount > 0 && (
                        <span className="flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 font-medium text-red-700">
                          <AlertOctagon className="h-3 w-3" />
                          blocked
                        </span>
                      )}

                      <StatusBadge tone="gray">
                        {STATUS_LABELS[item.status]}
                      </StatusBadge>
                      <StatusBadge tone={PRIORITY_TONE[item.priority]}>
                        {PRIORITY_LABELS[item.priority]}
                      </StatusBadge>
                      {item.severity && (
                        <StatusBadge tone="gray">
                          {SEVERITY_LABELS[item.severity]}
                        </StatusBadge>
                      )}

                      <span className="w-28 truncate text-right text-ink-600">
                        {item.assigneeName ?? "unassigned"}
                      </span>
                      <span className="tnum w-10 text-right text-ink-500">
                        {formatHours(item.currentEstimateHours)}
                      </span>
                      <span
                        className={cn(
                          "tnum w-24 text-right",
                          item.committedDueDate == null
                            ? "text-ink-400"
                            : item.scheduleVarianceDays > 0
                              ? "font-medium text-red-600"
                              : "text-ink-500",
                        )}
                        title={
                          item.committedDueDate
                            ? formatSlippage(item.scheduleVarianceDays)
                            : undefined
                        }
                      >
                        {item.committedDueDate ?? "no date"}
                      </span>

                      {canAssign && (
                        <div className="w-36 shrink-0 text-right">
                          {item.assigneeId === user?.employeeId ? (
                            <span className="text-ink-500">Yours</span>
                          ) : isClosedStatus(item.status) ? (
                            /* Claiming work that has stopped puts your name on something no
                               screen will ever show you again, because every list of what a
                               person has to do lists open work. Nothing is offered here. */
                            <span className="text-ink-400">closed</span>
                          ) : (
                            <AssignToMeButton
                              itemKey={item.itemKey}
                              asking={asking === item.id}
                              busy={
                                assignToMe.isPending &&
                                assignToMe.variables === item.id
                              }
                              onAsk={() => setAsking(item.id)}
                              onCancel={() => setAsking(null)}
                              onAssign={() => {
                                setAsking(null);
                                assignToMe.mutate(item.id);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </>
  );
}

/**
 * Claiming an item, asked for twice.
 *
 * <p>The claim buttons sit in a column of identical buttons against rows a search has just
 * reordered, which is the arrangement that produces mistaken clicks. So the button arms rather
 * than acts, and names the item it is about to hand over — the row a person means to claim and
 * the row under their cursor are not always the same one.
 */
function AssignToMeButton({
  itemKey,
  asking,
  busy,
  onAsk,
  onCancel,
  onAssign,
}: {
  itemKey: string;
  /** True while this row is waiting to be confirmed. Only ever one row at a time. */
  asking: boolean;
  busy: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onAssign: () => void;
}) {
  /* The key goes in the label rather than the face of the button: the column is as wide as the
     one word it was built for, and a pair of buttons spelling out "Take ACC-41" spilled over the
     dates to its left. The row is highlighted and carries its own key an inch away. */
  if (asking) {
    return (
      <div className="flex w-36 items-center justify-end gap-1">
        <Button
          size="sm"
          className="whitespace-nowrap"
          onClick={onAssign}
          aria-label={`Confirm: assign ${itemKey} to yourself`}
          autoFocus
        >
          Confirm
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} aria-label={`Leave ${itemKey} alone`}>
          No
        </Button>
      </div>
    );
  }

  return (
    <Button
      size="sm"
      variant="secondary"
      className="w-36 whitespace-nowrap"
      loading={busy}
      icon={<UserPlus className="h-3.5 w-3.5" />}
      onClick={onAsk}
      title={`Assign ${itemKey} to yourself`}
    >
      Assign to me
    </Button>
  );
}

/**
 * Naming and keeping the current filters.
 *
 * <p>A popover rather than a permanent name box. Saving is occasional and searching is constant,
 * so a field for the rare thing sat in the middle of the card doing nothing most of the time.
 *
 * <p>When a saved search is loaded, its name is already in the box. Adjusting a filter and saving
 * again is the commonest thing anybody does here, and leaving the field empty made the update path
 * depend on retyping the name exactly — which reads as "save" and silently means "replace".
 */
function SaveSearchControl({
  disabled,
  saving,
  loadedName,
  existingNames,
  onSave,
}: {
  disabled: boolean;
  saving: boolean;
  /** The saved search currently loaded, if any. Becomes the starting name. */
  loadedName: string;
  existingNames: string[];
  onSave: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(loadedName);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const submit = () => {
    if (name.trim() === "") {
      return;
    }
    onSave(name.trim());
    setOpen(false);
  };

  /* Compared the way the server compares it, so the button never promises the wrong verb. */
  const replaces = existingNames.some(
    (each) => each.trim().toLowerCase() === name.trim().toLowerCase(),
  );

  return (
    <div className="relative" ref={container}>
      <Button
        variant="secondary"
        aria-label="Save this search"
        title={disabled ? "Choose a filter first" : "Save this search"}
        disabled={disabled}
        icon={<Bookmark className="h-4 w-4" />}
        onClick={() => {
          setName(loadedName);
          setOpen((was) => !was);
        }}
      />

      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 rounded-lg border border-ink-200 bg-surface p-3 shadow-lg">
          <label className="mb-1 block text-xs font-medium text-ink-600" htmlFor="save-search-name">
            Name this search
          </label>
          <Input
            id="save-search-name"
            autoFocus
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                submit();
              }
              if (event.key === "Escape") {
                setOpen(false);
              }
            }}
            placeholder="LMS high priority"
            className="h-9"
          />
          <p className="mt-1.5 text-xs text-ink-500">
            {name.trim() === ""
              ? "Give it a name you will recognise later."
              : replaces
                ? `Replaces the filters saved under “${name.trim()}”.`
                : "Saved as a new search."}
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" loading={saving} disabled={name.trim() === ""} onClick={submit}>
              {replaces ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
